import { Trans } from '@lingui/react/macro';
import { QuoteStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, NoddiDummyButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { shouldPayForOrder } from '../../../utils/tireOfferPayment';

export const AcceptedActiveTireOffers = ({ userGroupId }: { userGroupId: number }) => {
  const {
    isPending: isTireSetOffersPending,
    data: tireSetOffers,
    error: tireSetOffersError
  } = noddiAsync.useGet({
    type: URLKeys.getCarTireQuotesIgnoringImpersonation,
    input: {
      userGroupIds: [userGroupId],
      statuses: [
        QuoteStatus.ACCEPTED,
        QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL,
        QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL,
        QuoteStatus.INVENTORY_RECEIVED_IN_FULL,
        QuoteStatus.INVENTORY_RECEIVED_PARTIAL,
        QuoteStatus.BOOKING_PROPOSAL_TIRE_MOUNT_SENT_TO_CUSTOMER,
        QuoteStatus.TIRE_MOUNTING_BOOKED
      ]
    }
  });

  if (isTireSetOffersPending) {
    return null;
  }

  if (tireSetOffersError || tireSetOffers.results.length === 0) {
    return null;
  }

  const hasMultipleOffers = tireSetOffers.results.length > 1;

  if (hasMultipleOffers) {
    return (
      <NoddiBasicCard
        variant='secondary'
        className='flex flex-col'
        to={`${customerRoutes.tireOfferHomePage.getPath()}?tab=accepted`}
      >
        <div className='flex items-center justify-between gap-2 md:gap-4'>
          <p className='text-xl font-bold text-primary md:text-3xl'>
            {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
            <Trans>You have multiple brand new tires on the way</Trans> 🎉
          </p>
        </div>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <p>
              <Trans>Check out the status page to see more information about your orders</Trans>
            </p>
          </div>

          <div className='flex w-full items-end justify-end'>
            <NoddiDummyButton size='small' endIcon='ArrowRight' variant='outline'>
              <Trans>View orders</Trans>
            </NoddiDummyButton>
          </div>
        </div>
      </NoddiBasicCard>
    );
  }

  const singleTireOffer = tireSetOffers.results[0]!;

  const orderAcceptedButShouldPay = shouldPayForOrder({
    paymentMethodInTireOffer: singleTireOffer.paymentMethod,
    isPaid: singleTireOffer.isPaid,
    isSelected: singleTireOffer.isSelected
  });

  return (
    <NoddiBasicCard
      variant='secondary'
      className='flex flex-col'
      to={customerRoutes.tireOfferStatus.getPath({ slug: singleTireOffer.slug })}
    >
      <div className='flex items-center justify-between gap-2 md:gap-4'>
        <p className='text-xl font-bold text-primary md:text-3xl'>
          {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
          <Trans>You have brand new tires on the way</Trans> 🎉
        </p>
      </div>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <p>
            {orderAcceptedButShouldPay ? (
              <Trans>Please pay for your order in order for us to process it.</Trans>
            ) : (
              <Trans>Check out the status page to see more information about your order</Trans>
            )}
          </p>
        </div>

        <div className='flex w-full items-end justify-end'>
          <NoddiButton size='small' endIcon='ArrowRight' variant='outline'>
            {orderAcceptedButShouldPay ? <Trans>Pay</Trans> : <Trans>View status</Trans>}
          </NoddiButton>
        </div>
      </div>
    </NoddiBasicCard>
  );
};
